import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import { faCircleQuestion } from '@fortawesome/pro-solid-svg-icons';
import { useSettings } from 'services/settings/SettingsContext';
import { shakeElement } from 'services/utils/shakeElement';
import { useFetchData } from 'services/api/useFetchData';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';
import { calculateCurrentSubscriptionPeriodFraction } from './functions/billingUtils';
import { saveData } from 'services/api/saveData';
import { useGlobalContext } from 'GlobalContext';
import { PaymentMethod, SubscriptionPackagePricing } from './BillingTypes';
import { fetchData } from 'services/api/fetchData';
import PaymentMethodForm from './billing-overview/PaymentMethodForm';
import { useStripe } from '@stripe/react-stripe-js';
import { handleManualPayment } from './functions/handleManualPayment';

const UpgradeSeatsForm: React.FC = () => {
    const { t } = useTranslation();
    const { setFloatingAlert } = useGlobalContext();
    const { environmentHash, handleLogout } = useAuthContext();
    const { userLocale, userTimezone } = useSettings();
    const { initializeModal, revealModal, closeModal, modalStack } = useModal();
    const stripe = useStripe();
    const currentDateTime = new Date();
    const utcDateTime = convertLocalDateTimeToUTC(currentDateTime, userLocale, userTimezone);
    const [error, setError] = useState<boolean>(false);
    const [duration, setDuration] = useState<string>('');
    const [activePaymentMethod, setActivePaymentMethod] = useState<PaymentMethod | null>(null);
    const [currentPackagePricing, setCurrentPackagePricing] = useState<SubscriptionPackagePricing | null>(null)
    const [subscriptionEndDate, setSubscriptionEndDate] = useState<string>('');
    const [hasFieldLicenses, setHasFieldLicenses] = useState<boolean>(false);
    const [hasLightLicenses, setHasLightLicenses] = useState<boolean>(false);
    const [seats, setSeats] = useState<{ normal: string; field?: string, light?: string }>({ normal: '0', field: '0', light: '0' });
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);
    const topModal = modalStack[modalStack.length - 1];

    // Fetch the manage seats data
    const { response } = useFetchData({ 
        apiUrl: `get_manage_seats_data/${environmentHash}`, 
        params: useMemo(() => ({ current_date: utcDateTime }), []) 
    });

    // Fetch the current payment method
    const { response: fetchedPaymentMethod, refetch: refetchPaymentMethod } = useFetchData({ 
        apiUrl: `get_payment_method/${environmentHash}`
    });
    
    // Reveal the modal after succesful response
    useEffect(() => {
        if (response) {            
            // Set the fetched duration
            setDuration(response.duration);

            // Set the current package pricing
            setCurrentPackagePricing(response.current_package_prices);

            // Set the has field / light licenses state
            setHasFieldLicenses(response.current_package_prices?.enable_field_seats ?? false);
            setHasLightLicenses(response.current_package_prices?.enable_light_seats ?? false);

            // Set the fetched subscription end date
            setSubscriptionEndDate(response.subscription_end_date);
            
            // Reveal the modal
            revealModal();
        }
    }, [response]);

    // Set the current payment method if it exists
    useEffect(() => {
        if (fetchedPaymentMethod) {
            setActivePaymentMethod(fetchedPaymentMethod);
        }
    }, [fetchedPaymentMethod]);

    // Handle seats change
    const handleSeatsChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'normal' | 'field' | 'light') => {
        const value = e.target.value;

        // Only allow input of numbers
        if (value === '' || /^[0-9]+$/.test(value)) {
            const numericValue = parseInt(value || '0', 10);
        
            // Show error if the given seats are lower than zero
            if (numericValue < 0) {

                // Show the error
                setError(true);

                // Shake the modal
                if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
            } else {
                setError(false);
            };

            // Update the seats state
            setSeats(prev => ({ ...prev, [type]: value }));
        }
    };

    // Handle incrementation of the seats field
    const handleIncrement = (type: 'normal' | 'field' | 'light') => {
        const current = parseInt(seats[type] || '0', 10);
        setSeats(prev => ({ ...prev, [type]: String(current + 1) }));
    };

    // Handle decrementation of the seats field
    const handleDecrement = (type: 'normal' | 'field' | 'light') => {
        const current = parseInt(seats[type] || '0', 10);
        if (current > 0) {
            setSeats(prev => ({ ...prev, [type]: String(current - 1) }));
        };
    };

    // Determine if the decrementation is disabled
    const isDecrementDisabled = (type: 'normal' | 'field' | 'light') => {
        const current = parseInt(seats[type] || '0', 10);
        return current <= 0;
    };

    // Get the right label with the right price
    const getLabelWithPrice = (type: 'normal' | 'field' | 'light'): string => {

        // Configure the package label based on the type
        const label = t(`billing.general.${type}_seats_label`)

        // When the current package pricing is fetched, return the label and price
        if (currentPackagePricing) {

            // Get the seat price based on the duration
            let price = duration === 'yearly' ? Number(currentPackagePricing.yearly_price) : Number(currentPackagePricing.monthly_price);

            // For field seats, set the price to the field price
            if (type === 'field') {
                price = duration === 'yearly' ? Number(currentPackagePricing.yearly_field_price) : Number(currentPackagePricing.monthly_field_price);
            };

            // For light seats, set the price to the light price
            if (type === 'light') {
                price = duration === 'yearly' ? Number(currentPackagePricing.yearly_light_price) : Number(currentPackagePricing.monthly_light_price);
            };

            // Check wheter the price has decimals. If the total has one decimal, convert it to two decimals
            const hasDecimals = price % 1 !== 0;
            const decimalPlaces = hasDecimals
                ? (price.toString().split('.')[1]?.length === 1 ? 2 : 2)
                : 0;

            // Format the amount based on the user locale
            const formattedPrice = new Intl.NumberFormat(userLocale, {
                style: "decimal",
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            }).format(price);

            return `${label} (€${formattedPrice}/${t('billing.general.per_month_short_label')})`;

        // If no package is found, just return the label
        } else {
            return label;
        };
    };

    // Calculate the new monthly seats amount
    const calculateNewMonthlySeatsAmount = ({ returnAsNumber }: { returnAsNumber?: boolean } = {}) => {

        // Handle if the current package pricing is fetched
        if (currentPackagePricing) {

            // Get the seat price based on the selected duration
            let normalSeatPrice = duration === 'yearly' ? Number(currentPackagePricing.yearly_price) : Number(currentPackagePricing.monthly_price);
            let fieldSeatPrice = duration === 'yearly' ? Number(currentPackagePricing.yearly_field_price) : Number(currentPackagePricing.monthly_field_price);
            let lightSeatPrice = duration === 'yearly' ? Number(currentPackagePricing.yearly_light_price) : Number(currentPackagePricing.monthly_light_price);

            // Initialize amount value
            let amount = 0;

            // Convert the entered seats to numbers
            let normalSeats = parseInt(seats.normal);
            let fieldSeats = parseInt(seats.field ?? '0');
            let lightSeats = parseInt(seats.light ?? '0');

            // Calculate the amount for the normal seats
            if (normalSeats > 0) {
                amount = normalSeats * normalSeatPrice;
            };

            // If field seats are entered, calculate the amount for the field seats
            if (fieldSeats > 0) {
                amount += fieldSeats * fieldSeatPrice;
            }

            // If light seats are entered, calculate the amount for the light seats
            if (lightSeats > 0) {
                amount += lightSeats * lightSeatPrice;
            }

            // Returns the raw amount as number if given
            if (returnAsNumber) {
                return amount;
            };

            // Check wheter the amount has decimals. If the amount has one decimal, convert it to two decimals
            const hasDecimals = amount % 1 !== 0;
            const decimalPlaces = hasDecimals
                ? (amount.toString().split('.')[1]?.length === 1 ? 2 : 2)
                : 0;

            // Format the amount based on the user locale
            const formattedAmount = new Intl.NumberFormat(userLocale, {
                style: "decimal",
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            }).format(amount);
    
            return formattedAmount;   
        }
    };

    // Calculate the remaining fraction of the current subscription in months
    const calculateRemainingFraction = () => {

        // Calculate the remaining subscription fraction
        const remainingSubscriptionFraction = Number(calculateCurrentSubscriptionPeriodFraction(subscriptionEndDate, duration));

        // Multiply the remaining fraction by the duration
        const totalDurationFraction = duration == 'yearly' ? remainingSubscriptionFraction * 12 : remainingSubscriptionFraction;

        // Round the fraction
        const rounded = Math.round(totalDurationFraction * 10) / 10;

        // Format in one decimal place
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
        }).format(rounded);

        return formattedAmount;
    };

    // Calculate the seats invoice amount excl vat
    const calculateSeatsInvoiceExVat = () => {

        // Calculate the remaining subscription fraction
        const remainingSubscriptionFraction = Number(calculateCurrentSubscriptionPeriodFraction(subscriptionEndDate, duration));

        // Calculate the new monthly seats amount
        const newMonthlySeatsAmount = Number(calculateNewMonthlySeatsAmount({ returnAsNumber: true }));

        // Multiply the monthly seats amount by the duration
        const totalDurationAmount = duration == 'yearly' ? newMonthlySeatsAmount * 12 : newMonthlySeatsAmount;

        // Calculate the seats invoice amount
        const invoiceAmount = totalDurationAmount * remainingSubscriptionFraction;

        // Format the amount based on the user locale
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(invoiceAmount);

        return formattedAmount;
    };

    // Calculate the seats invoice amount incl vat
    const calculateSeatsInvoiceAmount = () => {

        // Calculate the remaining subscription fraction
        const remainingSubscriptionFraction = Number(calculateCurrentSubscriptionPeriodFraction(subscriptionEndDate, duration));

        // Calculate the new monthly seats amount
        const newMonthlySeatsAmount = Number(calculateNewMonthlySeatsAmount({ returnAsNumber: true }));

        // Multiply the monthly seats amount by the duration
        const totalDurationAmount = duration == 'yearly' ? newMonthlySeatsAmount * 12 : newMonthlySeatsAmount;

        // Calculate the seats invoice amount
        const invoiceAmount = totalDurationAmount * remainingSubscriptionFraction;

        // Multiply the vat
        const invoiceAmountVat = invoiceAmount * 1.21;

        // Format the amount based on the user locale
        const formattedAmount = new Intl.NumberFormat(userLocale, {
            style: "decimal",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(invoiceAmountVat);

        return formattedAmount;
    };

    // Handle the submit of the upgraded seats
    const handleSubmit = async () => {

        // Start the button loader
        setButtonLoader(true);

        // When no active payment method exists, 
        if (!activePaymentMethod) {

            // Fetch the main company details
            const companyDetailsResponse = await fetchData({ 
                apiUrl: `get_main_company_details/${environmentHash}`,
                handleLogout
            });

            // Stop the button loader
            setButtonLoader(false);

            // When the company details are fetched, show the payment method form modal
            if (companyDetailsResponse) {
                initializeModal(
                    <PaymentMethodForm billingData={companyDetailsResponse} />, { 
                        title: t('billing.general.add_payment_method'),
                        modalSize: 'small-medium', 
                        onSuccess: () => {
                            refetchPaymentMethod()
                        },
                    }
                )
            }   
            
            return;
        }

        try {
            // Add the current date to look for a current subscription
            const currentLocalDateTime = new Date();

            // Configure the submit data
            const submitData = {
                seats: { 
                    normal: parseInt(seats.normal),
                    field: parseInt(seats.field ?? '0'),
                    light: parseInt(seats.light ?? '0')
                },
                current_datetime: currentLocalDateTime
            };

            // Post the data in the backend
            const response = await saveData({ 
                apiUrl: `post_seat/${environmentHash}`, 
                method: 'post', 
                data: submitData
            });

            // Handle successful response
            if (response?.status === 200) {

                // Start the ideal payment when an amount is due, the seats activate after the payment
                if (response.data.start_manual_payment === true) {

                    // Fetch the main company details for the ideal billing details
                    const companyDetailsResponse = await fetchData({
                        apiUrl: `get_main_company_details/${environmentHash}`,
                        handleLogout
                    });

                    // Redirect to the ideal payment page, the loader keeps running until the redirect
                    await handleManualPayment(
                        stripe,
                        environmentHash,
                        companyDetailsResponse,
                        response.data.subscription_id,
                        null,
                        null,
                        null,
                        response.data.new_seat_line_ids
                    );

                    return;
                }

                // Set the button loader to false
                setButtonLoader(false);

                // Show success alert
                setFloatingAlert({ type: 'success', message: 'billing.general.add_seats_success_message' });

                // Call the on success callback
                if (topModal?.props?.onSuccess) {
                    topModal.props.onSuccess(response.data);
                };

                // Close the modal
                closeModal();
            }
        } catch (error) {
            console.error("Error on submit", error);

            // Stop the button loader
            setButtonLoader(false);

            // Show an alert that the order could not be completed
            setFloatingAlert({ type: 'danger', message: 'billing.general.order_failed_message' });

            // Shake the modal in any case of an error
            if (topModal?.modalRef) shakeElement(topModal.modalRef.current as HTMLElement);
        };
    };

    // Render the seat manage rows
    const renderSeatManageRow = (label: string, type: 'normal' | 'field' | 'light') => (
        <div key={type}>
            <div className='seat-manage-row'>
                <div className='label'>
                    <label htmlFor={type}>
                        {label}
                    </label>
                    {type === 'light' && (
                        <span className="tooltip-icon">
                            <FontAwesomeIcon icon={faCircleQuestion} />
                            <span className="tooltip">{t('billing.general.light_seats_tooltipText')}</span>
                        </span>
                    )}
                </div>
                <div className='controls'>
                    <div className={`stepper-button ${isDecrementDisabled(type) ? 'disabled' : ''}`}
                         onClick={() => handleDecrement(type)}>
                        <FontAwesomeIcon icon={faMinus} />
                    </div>
                    <input
                        type='text'
                        id={type}
                        value={seats[type]}
                        onChange={(e) => handleSeatsChange(e, type)}
                        onKeyDown={(e) => { if (['.', ',', 'e'].includes(e.key)) e.preventDefault() }} // Prevent entering decimals and exponentials
                        className='seats-input'
                    />
                    <div className='stepper-button'
                         onClick={() => handleIncrement(type)}>
                        <FontAwesomeIcon icon={faPlus} />
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className='manage-seats-modal'>
            <div className='info'>
                {t('billing.general.add_seats_description')}
            </div>
            <div className='seat-manage-rows'>
                {renderSeatManageRow(getLabelWithPrice('normal'), 'normal')}
                {hasFieldLicenses && renderSeatManageRow(getLabelWithPrice('field'), 'field')}
                {hasLightLicenses && renderSeatManageRow(getLabelWithPrice('light'), 'light')}
            </div>
            {error && (
                <div className='error'>
                    {t('billing.general.minimal_seats_error')} 
                </div>
            )}
            {(parseInt(seats.normal) > 0 || parseInt(seats.field ?? '0') > 0 || parseInt(seats.light ?? '0') > 0) && (
                <div className='cost-breakdown'>
                    <h6>
                        {t('billing.general.add_seats_additional_costs')}
                    </h6>
                    <p>{t('billing.general.add_seats_monthly_total', { newMonthlySeatsAmount: calculateNewMonthlySeatsAmount() })}</p>
                    <p>{t('billing.general.remaining_months_total', { remainingFraction: calculateRemainingFraction() })}</p>
                    <p>{t('billing.general.add_seats_invoice_ex_vat', { invoiceExVat: calculateSeatsInvoiceExVat() })}</p>
                    <p className='strong'>{t('billing.general.add_seats_invoice_amount', { invoiceAmount: calculateSeatsInvoiceAmount() })}</p>
                    <p style={{ marginTop: '1rem' }}>
                        {t('billing.general.add_seats_order_label')}
                    </p>
                </div>
            )}
            <div className='buttons-right'>
                <SecondaryButton
                    onClick={() => closeModal()} 
                    label='general.cancel'
                    size="small"
                />
                <PrimaryButton
                    type="button" 
                    label='billing.general.order_subscription_button'
                    onClick={handleSubmit}
                    size="small"
                    onlyViewRestriction={true}
                    loading={buttonLoader}
                />
            </div>
        </div>
    )
};

export default UpgradeSeatsForm;